// 2026-09-22 (user request): Halaman DETAIL PENGELUARAN per project —
// daftar pengeluaran digrup PER MINGGU (Senin–Minggu). Dibuka dari tombol
// "Detail Pengeluaran" di Laporan Keuangan → tab Pengeluaran (menggantikan
// dropdown inline). Setiap baris minggu punya tombol "Detail Pengeluaran
// Mingguan" (kanan) → halaman rincian periode tersebut.
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { formatIDR } from "@/lib/constants";
import { mondayOf, weekLabelOf, buildUnifiedExpenses, rowsForHead } from "@/lib/expenseWeeks";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";

export default function PengeluaranProjectPage() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canPerm, ready: accessReady } = useAccess();
  const canView = canPerm(user, "finance_report", "view");
  const isOps = pid === "ops-kantor";

  const [projects, setProjects] = useState([]);
  const [pettyCash, setPettyCash] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [projRes, pcRes] = await Promise.all([
        api.get("/active-projects"),
        api.get("/petty-cash").catch(() => ({ data: [] })),
      ]);
      setProjects(Array.isArray(projRes.data) ? projRes.data : []);
      setPettyCash(Array.isArray(pcRes.data) ? pcRes.data : (pcRes.data?.items || []));
    } catch (e) {
      setLoadError("Server tidak merespons atau koneksi terputus.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (canView) load(); }, [canView]); // eslint-disable-line react-hooks/exhaustive-deps

  const head = useMemo(() => projects.find((p) => p.id === pid) || null, [projects, pid]);

  const rows = useMemo(
    () => rowsForHead(buildUnifiedExpenses(projects, pettyCash), pid),
    [projects, pettyCash, pid],
  );

  const weeks = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const key = mondayOf(r.date) || "tanpa-tanggal";
      if (!m.has(key)) m.set(key, { key, rows: [], total: 0 });
      const g = m.get(key);
      g.rows.push(r);
      g.total += r.amount;
    }
    for (const g of m.values()) g.rows.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    return [...m.values()].sort((a, b) => b.key.localeCompare(a.key));
  }, [rows]);

  const total = rows.reduce((s, r) => s + r.amount, 0);
  const title = isOps ? "Operasional Kantor (Petty Cash)" : (head?.project_name || head?.wo_number || "Detail Pengeluaran");

  if (!accessReady) {
    return <div className="p-6 text-sm text-slate-400 italic">Memuat izin akses…</div>;
  }
  if (!canView) {
    return (
      <div className="p-6" data-testid="pengeluaran-project-no-access">
        <div className="max-w-md mx-auto mt-16 border border-rose-200 bg-rose-50 p-6 text-center">
          <AlertTriangle size={28} className="text-rose-600 mx-auto mb-2" />
          <h2 className="font-display text-lg font-bold text-rose-800 mb-1">Akses Ditolak</h2>
          <p className="text-xs text-rose-700 leading-relaxed">Anda belum punya izin melihat <b>Laporan Keuangan</b>.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4" data-testid="pengeluaran-project-page">
      {/* Header + kembali */}
      <div className="flex items-start gap-3">
        <button type="button" onClick={() => navigate("/finance-report?tab=pengeluaran")}
          data-testid="pengeluaran-back"
          className="mt-1 inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 text-slate-600 hover:bg-slate-100">
          <ArrowLeft size={12} /> Kembali
        </button>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Keuangan · Pengeluaran · Detail</div>
          <h1 className="text-2xl font-bold text-[#0B1D3A] tracking-tight mt-0.5 font-display" data-testid="pengeluaran-project-title">{title}</h1>
          <p className="text-xs text-slate-500 mt-1 tabular-nums" data-testid="pengeluaran-project-subtitle">
            {!isOps && head?.client ? `${head.client} · ` : ""}{rows.length} pengeluaran · Total <b className="text-rose-700">{formatIDR(total)}</b>
          </p>
        </div>
      </div>

      {loadError && (
        <div className="border border-rose-200 bg-rose-50 p-6 text-center" data-testid="pengeluaran-project-error">
          <AlertTriangle size={24} className="mx-auto text-rose-400 mb-2" />
          <div className="text-sm font-bold text-rose-700">Gagal memuat data</div>
          <div className="text-xs text-rose-600 mt-1 mb-3">{loadError} Data Anda aman — ini masalah sementara.</div>
          <button type="button" onClick={load} data-testid="pengeluaran-project-retry"
            className="px-4 py-2 text-xs font-semibold bg-[#0B1D3A] text-white hover:bg-[#12294f]">Coba Lagi</button>
        </div>
      )}

      {loading && rows.length === 0 ? (
        <div className="p-8 text-center text-xs italic text-slate-400" data-testid="pengeluaran-project-loading">Memuat pengeluaran…</div>
      ) : !loading && !isOps && !head ? (
        <div className="p-8 text-center text-xs italic text-slate-400 border border-slate-200" data-testid="pengeluaran-project-notfound">
          Project tidak ditemukan.
        </div>
      ) : (
        <div className="border border-slate-200 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b-2 border-slate-300">
              <tr className="text-left">
                <th className="px-3 py-2 overline text-slate-700">Periode (Senin–Minggu)</th>
                <th className="px-3 py-2 overline text-slate-700">Pengeluaran</th>
                <th className="px-3 py-2 overline text-slate-700 text-right">Total</th>
                <th className="px-3 py-2 overline text-slate-700 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {weeks.length === 0 && (
                <tr><td colSpan={4} className="text-center py-8 text-slate-400 italic" data-testid="pengeluaran-project-empty">Belum ada pengeluaran pada grup ini.</td></tr>
              )}
              {weeks.map((w) => (
                <tr key={w.key} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`pengeluaran-week-row-${w.key}`}>
                  <td className="px-3 py-2.5 font-semibold text-slate-800">{weekLabelOf(w.key)}</td>
                  <td className="px-3 py-2.5 text-slate-500 tabular-nums">{w.rows.length} pengeluaran</td>
                  <td className="px-3 py-2.5 tabular-nums text-right font-bold text-rose-700">{formatIDR(w.total)}</td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <button type="button"
                      onClick={() => navigate(`/finance-report/pengeluaran/${pid}/minggu/${w.key}`)}
                      data-testid={`pengeluaran-week-detail-${w.key}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 text-[#0B1D3A] hover:bg-[#0B1D3A] hover:text-white transition-colors">
                      Detail Pengeluaran Mingguan <ChevronRight size={11} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
